const SOURCE_LANGUAGE = "en";
const LOCALE_PREFIX_PATTERN = /^[a-z]{2}(?:-[a-z]{2})?$/i;
const ABSOLUTE_ROUTE_PATTERN = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;
const WEGLOT_TIMEOUT_MS = 2000;
const WEGLOT_POLL_INTERVAL_MS = 50;
const KLAVIYO_POLL_INTERVAL_MS = 500;
const KLAVIYO_POLL_LIMIT = 40;
const KLAVIYO_FORM_SELECTOR =
  'form[data-testid^="klaviyo-form-"], form[data-newsletter-form]';
const translationCache = new Map();
let klaviyoLanguageSyncStarted = false;

function currentLocalePrefix() {
  const [firstSegment = ""] = window.location.pathname
    .split("/")
    .filter(Boolean);

  return LOCALE_PREFIX_PATTERN.test(firstSegment) ? `/${firstSegment}` : "";
}

export function getLocalisedRoute(route = "/") {
  if (!route || route.startsWith("#")) {
    return route;
  }

  let path = route.startsWith("/") ? route : `/${route}`;

  if (ABSOLUTE_ROUTE_PATTERN.test(route)) {
    const url = new URL(route, window.location.origin);

    if (url.origin !== window.location.origin) {
      return route;
    }

    path = `${url.pathname}${url.search}${url.hash}`;
  }

  const localePrefix = currentLocalePrefix();

  if (
    !localePrefix ||
    path === localePrefix ||
    path.startsWith(`${localePrefix}/`)
  ) {
    return path;
  }

  return path === "/" ? `${localePrefix}/` : `${localePrefix}${path}`;
}

function currentLanguage() {
  const urlLanguage = currentLocalePrefix().slice(1);
  const language =
    window.Weglot?.getCurrentLang?.() ||
    urlLanguage ||
    document.documentElement.lang ||
    SOURCE_LANGUAGE;

  return language.toLowerCase().split("-")[0];
}

function preferredSearchTerm(query) {
  const normalisedQuery = query.trim().toLowerCase();
  const preferredTerms = {
    pantalon: "trousers",
    pantalón: "trousers",
    pantalones: "trousers",
  };

  return (
    preferredTerms[normalisedQuery] ||
    query.replace(/\bpants\b/gi, "trousers")
  );
}

function waitForWeglot(
  isReady = (weglot) => Boolean(weglot),
  timeoutMs = WEGLOT_TIMEOUT_MS,
) {
  if (isReady(window.Weglot)) {
    return Promise.resolve(window.Weglot);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (isReady(window.Weglot)) {
        window.clearInterval(interval);
        resolve(window.Weglot);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(interval);
        resolve(null);
      }
    }, WEGLOT_POLL_INTERVAL_MS);
  });
}

function waitForWeglotSearch() {
  return waitForWeglot((weglot) => Boolean(weglot?.search));
}

export function waitForWeglotApi() {
  return waitForWeglot(
    (weglot) =>
      Boolean(weglot?.getCurrentLang) &&
      Boolean(weglot?.switchTo) &&
      Boolean(weglot?.options?.language_from),
    5000,
  );
}

async function translateSearchQuery(query) {
  const normalisedQuery = query.trim();

  if (!normalisedQuery || currentLanguage() === SOURCE_LANGUAGE) {
    return query;
  }

  const cacheKey = `${currentLanguage()}:${normalisedQuery}`;

  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  const weglot = await waitForWeglotSearch();

  if (!weglot) {
    return query;
  }

  return new Promise((resolve) => {
    const timeout = window.setTimeout(
      () => resolve(query),
      WEGLOT_TIMEOUT_MS,
    );

    try {
      weglot.search(normalisedQuery, (translatedQuery) => {
        window.clearTimeout(timeout);

        const preferredQuery = preferredSearchTerm(
          translatedQuery || normalisedQuery,
        );

        if (preferredQuery !== normalisedQuery) {
          translationCache.set(cacheKey, preferredQuery);
        }

        resolve(preferredQuery);
      });
    } catch {
      window.clearTimeout(timeout);
      resolve(query);
    }
  });
}

export async function translateWeglotSearchRequests(requests) {
  if (currentLanguage() === SOURCE_LANGUAGE) {
    return requests;
  }

  const queries = [
    ...new Set(
      requests
        .map((request) => new URLSearchParams(request.params).get("query"))
        .filter(Boolean),
    ),
  ];

  if (!queries.length) {
    return requests;
  }

  const translations = new Map(
    await Promise.all(
      queries.map(async (query) => [query, await translateSearchQuery(query)]),
    ),
  );

  return requests.map((request) => {
    const params = new URLSearchParams(request.params);
    const query = params.get("query");
    const translatedQuery = translations.get(query);

    if (!translatedQuery || translatedQuery === query) {
      return request;
    }

    params.set("query", translatedQuery);

    return {
      ...request,
      params: params.toString(),
    };
  });
}

export function startKlaviyoLanguageSync() {
  if (klaviyoLanguageSyncStarted) {
    return;
  }

  klaviyoLanguageSyncStarted = true;
  let lastIdentifiedLanguage = "";

  const identify = (language, force = false) => {
    const activeLanguage = (
      language ||
      window.Weglot?.getCurrentLang?.() ||
      document.documentElement.lang
    )
      ?.toLowerCase()
      .split("-")[0];

    if (
      !activeLanguage ||
      !document.querySelector(KLAVIYO_FORM_SELECTOR) ||
      !window.klaviyo?.push ||
      (!force && lastIdentifiedLanguage === activeLanguage)
    ) {
      return;
    }

    window.klaviyo.push(["identify", { lang: activeLanguage }]);
    lastIdentifiedLanguage = activeLanguage;
  };

  const bindWeglotEvents = () => {
    if (!window.Weglot?.on) {
      return false;
    }

    window.Weglot.on("languageChanged", (language) =>
      identify(language, true),
    );
    window.Weglot.on("switchersReady", identify);
    identify();

    return true;
  };

  document.addEventListener(
    "submit",
    (event) => {
      if (event.target.matches?.(KLAVIYO_FORM_SELECTOR)) {
        identify(null, true);
      }
    },
    true,
  );

  if (bindWeglotEvents()) {
    return;
  }

  let pollCount = 0;
  const interval = window.setInterval(() => {
    pollCount += 1;

    if (bindWeglotEvents() || pollCount >= KLAVIYO_POLL_LIMIT) {
      window.clearInterval(interval);
    }
  }, KLAVIYO_POLL_INTERVAL_MS);
}
